import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ArrowRight, Clock, Plus, Wallet, CalendarClock, AlertTriangle, Users } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useFinance } from "@/context/FinanceContext";
import { computeDailyHealth } from "@/lib/dailyHealth";
import { weeklyRecap, currentWeekKey } from "@/lib/insights";
import { DailyHealthCompact } from "@/components/DailyHealth";
import { WeeklyRecapBanner } from "@/components/WeeklyRecap";
import { LogReminderBanner } from "@/components/LogReminder";
import { RenewReminderBanner } from "@/components/RenewReminder";
import { MonthlySummaryPrompt } from "@/components/MonthlySummaryPrompt";
import { CommunityAnnouncement } from "@/components/CommunityAnnouncement";
import { StreakCard } from "@/components/StreakCard";
import { formatCompact, formatDateID } from "@/lib/currency";
import { simulationAccessLabel } from "@/lib/storage";
import { subscriptionLabel } from "@/lib/subscription";

export default function Dashboard() {
  const navigate = useNavigate();
  const { report, premium, simulationActive, subscription, subscriptionUntil } = useApp();
  const { finance, dismissWeeklyRecap } = useFinance();
  const health = useMemo(() => computeDailyHealth(finance), [finance]);
  const recap = useMemo(() => weeklyRecap(finance), [finance]);
  const weekKey = currentWeekKey();
  const showRecap = recap.available && finance?.ui?.recapDismissedWeek !== weekKey;

  const familyName = report?.data?.familyName || finance?.household?.name || "Keluarga Anda";
  const ready = health.status === "ready";
  const topWarning = health.warnings?.find((w) => w.level === "danger" || w.level === "warning");

  return (
    <div className="px-6 pt-8" data-testid="dashboard-home">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-xs text-zinc-500">Selamat datang</p>
          <h1 className="font-heading text-xl font-extrabold text-zinc-900">{familyName}</h1>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-900">
          <ShieldCheck className="h-5 w-5 text-emerald-400" />
        </div>
      </div>

      <LogReminderBanner className="mb-4" />
      <CommunityAnnouncement className="mb-4" />
      <MonthlySummaryPrompt className="mb-4" />
      <RenewReminderBanner className="mb-4" />
      <StreakCard className="mb-4" />

      {premium && subscription?.source === "trial" && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="trial-banner">
          <p className="font-heading text-sm font-bold text-amber-800">Masa coba gratis 3 hari aktif 🎉</p>
          <p className="mt-1 text-xs leading-relaxed text-amber-700">
            Semua fitur Premium terbuka hingga {formatDateID(subscriptionUntil)}. Berlangganan agar akses tidak terputus.
          </p>
        </div>
      )}

      {premium && simulationActive && (
        <div
          className="mb-4 flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2.5"
          data-testid="simulation-active-banner"
        >
          <Clock className="h-4 w-4 text-emerald-600" />
          <span className="text-xs font-medium text-emerald-700">
            {subscriptionLabel(subscriptionUntil, Date.now(), formatDateID)}
          </span>
        </div>
      )}

      {/* Desktop: grid 2 kolom profesional (inti kiri, aksi kanan); mobile: menumpuk */}
      <div className="lg:grid lg:grid-cols-12 lg:gap-6 lg:items-start">
        <div className="lg:col-span-7">
          {/* Primary: Kondisi Keuangan Hari Ini (Health Score) */}
          <p className="mb-2 font-heading text-sm font-bold text-zinc-900">Kondisi Keuangan Hari Ini</p>
          <DailyHealthCompact health={health} onOpen={() => navigate("/app/catatan?tab=analisis")} />

          {/* Warning penting */}
          {topWarning && (
            <div
              className={`mt-4 flex items-start gap-3 rounded-2xl border p-4 ${
                topWarning.level === "danger" ? "border-rose-100 bg-rose-50" : "border-amber-100 bg-amber-50"
              }`}
              data-testid="dashboard-warning"
            >
              <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${topWarning.level === "danger" ? "text-rose-500" : "text-amber-500"}`} />
              <div>
                <p className={`font-heading text-sm font-bold ${topWarning.level === "danger" ? "text-rose-700" : "text-amber-700"}`}>{topWarning.title}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-zinc-600">{topWarning.message}</p>
              </div>
            </div>
          )}

          {ready && (
            <div className="mt-4 grid grid-cols-2 gap-3" data-testid="dashboard-budget-forecast">
              <div className="rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow">
                <div className="flex items-center gap-1.5 text-zinc-500">
                  <Wallet className="h-3.5 w-3.5" />
                  <p className="text-xs">Sisa budget bln ini</p>
                </div>
                <p className={`mt-1 font-mono text-xl font-black ${health.forecast.safeToSpendTotal >= 0 ? "text-zinc-900" : "text-rose-600"}`}>
                  {health.forecast.safeToSpendTotal >= 0 ? "" : "-"}{formatCompact(Math.abs(health.forecast.safeToSpendTotal))}
                </p>
              </div>
              <div className="rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow">
                <div className="flex items-center gap-1.5 text-zinc-500">
                  <CalendarClock className="h-3.5 w-3.5" />
                  <p className="text-xs">Forecast akhir bln</p>
                </div>
                <p className={`mt-1 font-mono text-xl font-black ${health.forecast.projectedEndBalance >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                  {health.forecast.projectedEndBalance >= 0 ? "" : "-"}{formatCompact(Math.abs(health.forecast.projectedEndBalance))}
                </p>
              </div>
            </div>
          )}

          {showRecap && (
            <WeeklyRecapBanner
              recap={recap}
              onOpen={() => navigate("/app/catatan?tab=analisis")}
              onDismiss={() => dismissWeeklyRecap(weekKey)}
            />
          )}
        </div>

        <div className="lg:col-span-5">
          {/* Quick Entry */}
          <button
            data-testid="dashboard-quick-entry"
            onClick={() => navigate("/app/catat")}
            className="mt-4 flex w-full items-center gap-3 rounded-3xl bg-emerald-600 p-5 text-left text-white shadow-lg shadow-emerald-600/20 transition-transform active:scale-[0.99] lg:mt-0"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/20">
              <Plus className="h-6 w-6" strokeWidth={2.5} />
            </span>
            <div className="flex-1">
              <p className="font-heading text-sm font-bold">Catat pemasukan / pengeluaran</p>
              <p className="text-xs text-emerald-50/90">Beberapa detik untuk menjaga catatan tetap akurat.</p>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0" />
          </button>

          {/* Komunitas Premium */}
          <button
            data-testid="dashboard-community"
            onClick={() => navigate("/app/komunitas")}
            className="mt-4 flex w-full items-center gap-3 rounded-3xl border border-zinc-200 bg-white p-5 text-left shield-shadow transition-transform active:scale-[0.99]"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50">
              <Users className="h-5 w-5 text-emerald-600" />
            </span>
            <div className="flex-1">
              <p className="font-heading text-sm font-bold text-zinc-900">Komunitas Premium CeKKeU</p>
              <p className="text-xs text-zinc-500">Berbagi pengalaman & tips menuju keuangan lebih sehat.</p>
            </div>
            <ArrowRight className="h-5 w-5 shrink-0 text-zinc-300" />
          </button>

          {/* Insight terbaru → Analisis */}
          <button
            data-testid="dashboard-open-analisis"
            onClick={() => navigate("/app/catatan?tab=analisis")}
            className="mt-4 flex w-full items-center justify-between rounded-3xl border border-zinc-200 bg-white p-5 text-left shield-shadow"
          >
            <div>
              <p className="font-heading text-sm font-bold text-zinc-900">Insight & Target Hemat</p>
              <p className="text-xs text-zinc-500">Ringkasan bulanan, tren, dan progres target Anda.</p>
            </div>
            <ArrowRight className="h-5 w-5 text-zinc-400" />
          </button>

          {/* Ketahanan (feature, not a gate) */}
          <button
            data-testid="dashboard-open-resilience"
            onClick={() => navigate("/app/ketahanan")}
            className="mt-4 flex w-full items-center justify-between rounded-3xl border border-zinc-200 bg-white p-5 text-left shield-shadow"
          >
            <div className="min-w-0">
              <p className="font-heading text-sm font-bold text-zinc-900">Ketahanan Keuangan</p>
              {report ? (
                <p className="text-xs text-zinc-500">
                  Skor terakhir {report.base.score} · {report.base.status}
                  {report.createdAt ? ` · ${formatDateID(report.createdAt)}` : ""}
                </p>
              ) : (
                <p className="text-xs text-zinc-500">Belum diperiksa — cek ketahanan keluarga (±2 menit).</p>
              )}
            </div>
            <ArrowRight className="h-5 w-5 shrink-0 text-zinc-400" />
          </button>
        </div>
      </div>
    </div>
  );
}
